import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, Clock, XCircle, Building2, Plus, FileText } from 'lucide-react';
import { passAdmin } from '../../services/passApi';

const TABS = [
  ['pending', 'Pendientes'],
  ['paid', 'Pagados'],
  ['cancelled', 'Cancelados'],
  ['', 'Todos'],
];

function thisMonthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const fmt = (d) => d.toISOString().slice(0, 10);
  return { from: fmt(start), to: fmt(end) };
}

export default function PassSettlements() {
  const [tab, setTab] = useState('pending');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [showGen, setShowGen] = useState(false);
  const [genForm, setGenForm] = useState(() => thisMonthRange());
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  async function load() {
    setLoading(true);
    try {
      const res = await passAdmin.listSettlements(tab ? { status: tab } : {});
      setRows(res.data.settlements || []);
    } catch (err) {
      toast.error('Error cargando settlements');
    } finally {
      setLoading(false);
    }
  }

  async function handleGenerate(e) {
    e.preventDefault();
    setGenerating(true);
    try {
      const res = await passAdmin.generateSettlements({
        period_start: genForm.from || null,
        period_end: genForm.to || null,
      });
      toast.success(`${res.data.count} settlement(s) creados`);
      setShowGen(false);
      setTab('pending');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error generando');
    } finally {
      setGenerating(false);
    }
  }

  async function decide(id, status) {
    setBusyId(id);
    try {
      await passAdmin.decideSettlement(id, { status, method: status === 'paid' ? 'SPEI' : null });
      toast.success(`Marcado como ${status}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <DashboardLayout>
      <div className="px-6 py-6">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
          <h1 className="text-2xl font-bold text-slate-900 inline-flex items-center gap-2">
            <Building2 className="w-6 h-6 text-teal-600" /> Settlements a negocios — Pass
          </h1>
          <button
            onClick={() => setShowGen((v) => !v)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold"
          >
            <Plus className="w-4 h-4" /> Generar settlements
          </button>
        </div>
        <p className="text-sm text-slate-500 mb-5">
          Cuentas por pagar al negocio: cobros online recibidos menos fees de plataforma y comisiones de afiliados.
        </p>

        {showGen && (
          <form onSubmit={handleGenerate} className="bg-white border border-slate-200 rounded-xl p-4 mb-5 space-y-3 max-w-xl">
            <h3 className="font-semibold text-slate-900 text-sm">Periodo a liquidar</h3>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs text-slate-500">Desde</span>
                <input type="date" value={genForm.from} onChange={(e) => setGenForm((f) => ({ ...f, from: e.target.value }))} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              </label>
              <label className="block">
                <span className="text-xs text-slate-500">Hasta</span>
                <input type="date" value={genForm.to} onChange={(e) => setGenForm((f) => ({ ...f, to: e.target.value }))} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              </label>
            </div>
            <p className="text-xs text-slate-500">
              Solo se incluyen reservaciones con pago online completado que aún no estén en un settlement.
            </p>
            <div className="flex gap-2">
              <button type="submit" disabled={generating} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold disabled:opacity-60">
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Generar ahora
              </button>
              <button type="button" onClick={() => setShowGen(false)} className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm">Cancelar</button>
            </div>
          </form>
        )}

        <div className="flex gap-2 mb-4 overflow-x-auto">
          {TABS.map(([v, l]) => (
            <button
              key={v || 'all'}
              onClick={() => setTab(v)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                tab === v ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {l}
            </button>
          ))}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : rows.length === 0 ? (
            <p className="text-center text-slate-500 py-10">Sin settlements para este filtro.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2">Negocio</th>
                  <th className="text-left px-4 py-2">Periodo</th>
                  <th className="text-right px-4 py-2">Cobrado</th>
                  <th className="text-right px-4 py-2">Fee plataforma</th>
                  <th className="text-right px-4 py-2">Afiliados</th>
                  <th className="text-right px-4 py-2">Neto al negocio</th>
                  <th className="text-center px-4 py-2"># Res</th>
                  <th className="text-left px-4 py-2">Estado</th>
                  <th className="text-right px-4 py-2">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {rows.map((s) => (
                  <React.Fragment key={s.id}>
                    <tr>
                      <td className="px-4 py-2">
                        <button
                          onClick={() => setExpanded((e) => e === s.id ? null : s.id)}
                          className="text-left hover:underline text-slate-900"
                        >
                          {s.business_name || s.business_id}
                        </button>
                      </td>
                      <td className="px-4 py-2 text-xs text-slate-500">
                        {s.period_start || '—'} → {s.period_end || '—'}
                      </td>
                      <td className="px-4 py-2 text-right">${Number(s.gross_collected).toFixed(2)}</td>
                      <td className="px-4 py-2 text-right text-red-600">−${Number(s.platform_fee_total).toFixed(2)}</td>
                      <td className="px-4 py-2 text-right text-red-600">−${Number(s.affiliate_commissions_total).toFixed(2)}</td>
                      <td className="px-4 py-2 text-right font-bold text-slate-900">${Number(s.amount_due_to_business).toFixed(2)}</td>
                      <td className="px-4 py-2 text-center text-xs">{s.reservation_count}</td>
                      <td className="px-4 py-2"><StatusBadge status={s.status} /></td>
                      <td className="px-4 py-2 text-right">
                        <div className="inline-flex gap-1">
                          {s.status === 'pending' && (
                            <>
                              <ActionBtn label="Pagado" onClick={() => decide(s.id, 'paid')} busy={busyId === s.id} variant="green" />
                              <ActionBtn label="Cancelar" onClick={() => decide(s.id, 'cancelled')} busy={busyId === s.id} variant="red" />
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expanded === s.id && (
                      <tr>
                        <td colSpan={9} className="px-4 py-3 bg-slate-50">
                          <p className="text-xs text-slate-500 font-semibold mb-1">Reservaciones incluidas ({s.reservation_ids?.length || 0})</p>
                          <p className="text-xs font-mono text-slate-600 break-all">
                            {(s.reservation_ids || []).slice(0, 20).join(', ')}
                            {(s.reservation_ids?.length || 0) > 20 && ' …'}
                          </p>
                          {s.notes && <p className="text-xs text-slate-500 mt-2">Notas: {s.notes}</p>}
                          {s.method && <p className="text-xs text-slate-500">Método: {s.method}</p>}
                          {s.paid_at && <p className="text-xs text-slate-500">Pagado: {new Date(s.paid_at).toLocaleString()}</p>}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending:   { label: 'Pendiente', icon: Clock,         cls: 'bg-amber-50 text-amber-700' },
    paid:      { label: 'Pagado',    icon: CheckCircle2,  cls: 'bg-green-50 text-green-700' },
    cancelled: { label: 'Cancelado', icon: XCircle,       cls: 'bg-red-50 text-red-700' },
  };
  const m = map[status] || { label: status, icon: Clock, cls: 'bg-slate-100 text-slate-700' };
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${m.cls}`}>
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
}

function ActionBtn({ label, onClick, busy, variant }) {
  const colors = {
    green: 'bg-green-600 hover:bg-green-700',
    red:   'bg-red-600 hover:bg-red-700',
  };
  return (
    <button onClick={onClick} disabled={busy}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-white text-xs font-semibold disabled:opacity-60 ${colors[variant] || 'bg-slate-700'}`}>
      {busy && <Loader2 className="w-3 h-3 animate-spin" />} {label}
    </button>
  );
}
